"use client";

import { useMemo, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  ArrowUpRight,
  CircleCheck,
  Clapperboard,
  FileText,
  Film,
  Frame,
  Layers,
  MessageSquare,
  Palette,
  Ratio,
  Scan,
  Sparkles,
  Trash2,
  UserRound,
} from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-fetch";
import { Button } from "@/components/ui/button";
import { MoreMenu } from "@/components/ui/more-menu";
import { thumbUrl } from "@/lib/utils/thumb-url";
import { resolveArtStyleLabel } from "@/lib/ai/visual-settings";
import type { ProjectCoverSource } from "@/lib/projects/project-cover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { formatCount, relativeTimeOf, type ProjectStage } from "@/lib/project-stats";

export interface ProjectCardProps {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  /** 最近一次改动（DB 的 updated_at）。比 createdAt 更能说明「这个项目还在动」 */
  updatedAt?: string | null;
  /** 视觉风格标签（可能很长，卡片上截断 + title 全文） */
  artStyle?: string | null;
  episodeCount: number;
  characterCount: number;
  shotCount: number;
  videoShotCount: number;
  finalVideoUrl?: string | null;
  /** 阶段视觉映射 */
  stage: ProjectStage;
  videoProgress: number;

  // ── 2026-10-07 视觉改版新增 ─────────────────────────────────
  //改版前的卡片只有「集/角色/分镜」三个数字，整屏 17 张卡信息密度极低
  //（用户反馈：太松散、元素单一、层次不明）。下面这些是**库里已有**的数据，
  // 改版前要点进项目才看得到，现在一屏可见。
  sceneCount: number;
  propCount: number;
  dialogueCount: number;
  /** 已有成片的集数 */
  finishedEpisodeCount: number;
  /** 集数完成度 0~1 */
  episodeProgress: number;
  /** 画幅比例；schema 上有 default但老行可能为 null/空，卡片上按存在与否显示 */
  aspectRatio: string | null;
  generationMode: string;
  /** 风格三件套；库里大多数为null，卡片上按存在与否显示 */
  colorPalette: string | null;
  mood: string | null;
  eraAesthetics: string | null;
  /** 封面图；null = 该项目确实没有可用图片，走渐变占位 */
  cover: { url: string; source: ProjectCoverSource } | null;
}

/**
 * 阶段视觉表：**一个阶段 = 一套颜色 + 一个图标**。
 *
 * 为什么不用 `status` 驱动（原实现就是）：实测库里 17 个项目 status 全是 draft，
 * 三种状态配置里两种永远显示不出来，卡片之间毫无区分度。详见 lib/project-stats.ts 开头。
 *
 * 色相刻意分散（石板/琥珀/紫/蓝/青/绿），而不是同色系深浅 ——
 * 一屏里 17 张卡并排，同色系只能靠亮度差分辨，深浅一压缩就糊成一片。
 */
const STAGE_STYLE: Record<
  ProjectStage,
  { icon: typeof FileText; chip: string; bar: string; dot: string }
> = {
  empty: {
    icon: FileText,
    chip: "bg-slate-100 text-slate-600 ring-slate-200",
    bar: "bg-slate-300",
    dot: "bg-slate-400",
  },
  outline: {
    icon: Clapperboard,
    chip: "bg-amber-50 text-amber-700 ring-amber-200",
    bar: "bg-amber-400",
    dot: "bg-amber-500",
  },
  scripting: {
    icon: FileText,
    chip: "bg-violet-50 text-violet-700 ring-violet-200",
    bar: "bg-violet-400",
    dot: "bg-violet-500",
  },
  storyboard: {
    icon: Layers,
    chip: "bg-sky-50 text-sky-700 ring-sky-200",
    bar: "bg-sky-400",
    dot: "bg-sky-500",
  },
  producing: {
    // 🔴 不能用 Loader2 + animate-spin（2026-10-07 用户反馈）：
    // producing 只是「项目推进到了出片阶段」（有分镜、部分已成片），
    // **不代表后台真的有渲染任务在跑**。静态项目也转圈 = 谎报运行状态，
    // 用户会以为卡在加载。用胶片盘（Film）静态表达「视频产出阶段」。
    icon: Film,
    chip: "bg-cyan-50 text-cyan-700 ring-cyan-200",
    bar: "bg-cyan-400",
    dot: "bg-cyan-500",
  },
  done: {
    icon: CircleCheck,
    chip: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    bar: "bg-emerald-500",
    dot: "bg-emerald-500",
  },
};

/**
 * 无封面时的 16:9 占位渐变 —— **按阶段取色**，而不是全用同一个灰。
 *
 * 为什么不能统一用灰：17 张卡里有 3 张没图（测试/ 005 / 代理）。如果这三张是灰块，
 * 它们在网格里就成了三个"坏掉"的洞，视觉上比有图但不好看更糟。
 * 用阶段色渐变后，无图项目仍然传达"它做到哪一步"，与有图卡的徽章一致。
 *
 * 色相与 {@link STAGE_STYLE} 的 chip 同源，但用深一档的尾色，
 * 保证白色文字压在上面对比度足够（浅色chip 底 + 白字会看不清）。
 */
const FALLBACK_GRADIENT: Record<ProjectStage, string> = {
  empty: "from-slate-500 to-slate-700",
  outline: "from-amber-500 to-amber-700",
  scripting: "from-violet-500 to-violet-700",
  storyboard: "from-sky-500 to-sky-700",
  producing: "from-cyan-600 to-cyan-800",
  done: "from-emerald-500 to-emerald-700",
};

/**
 * 封面来源 → 角标文案 key。
 *
 * 为什么要标来源：一张「镜头画面」和一张「角色立绘」代表的项目完成度完全不同，
 * 用户看到封面就能判断这项目做到哪了 —— 角标只是把这件事说明白，不必点进去。
 */
const COVER_SOURCE_LABEL_KEY: Record<ProjectCoverSource, string> = {
  shot: "coverFromShot",
  asset: "coverFromAsset",
  scene: "coverFromScene",
  prop: "coverFromProp",
  character: "coverFromCharacter",
};

export function ProjectCard(props: ProjectCardProps) {
  const t = useTranslations("dashboard");
  const tc = useTranslations("common");
  const locale = useLocale();
  const router = useRouter();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** 后端 409 回来的「会连带删掉什么」：非空即渲染二次确认块 */
  const [blocked, setBlocked] = useState<{ code: string; counts: Record<string, number> } | null>(null);
  const [coverBroken, setCoverBroken] = useState(false);

  const {
    id,
    title,
    createdAt,
    updatedAt,
    artStyle,
    episodeCount,
    characterCount,
    shotCount,
    videoShotCount,
    sceneCount,
    propCount,
    dialogueCount,
    finishedEpisodeCount,
    stage,
    videoProgress,
    episodeProgress,
    aspectRatio,
    generationMode,
    cover,
  } = props;

  const style = STAGE_STYLE[stage];
  const StageIcon = style.icon;

  /**
   * 封面加载失败 → 退回渐变占位。
   *
   * 🔴 不能靠"库里 file_url 非空"就认为图一定打得开：实测库里的路径指向磁盘真实文件，
   * 但`uploads/` 下被清理过的历史产物仍会404。封面 404 而卡片其余部分正常，
   * 就是一个 16:9 的破洞 —— 必须在 `<img>` 的 onError 里自己兜住。
   */
  const coverSrc = useMemo(() => {
    if (!cover || coverBroken) return "";
    return thumbUrl(cover.url, 480);
  }, [cover, coverBroken]);

  /** 无封面时的占位配色：按阶段取色相，与徽章/进度条同源，整卡不会三方色系打架 */
  const fallbackGradient = FALLBACK_GRADIENT[stage];

  // 时间口径：**优先 updatedAt**。用户回到列表页想知道「哪个项目还在动」，
  // 而 createdAt 只是出生证明。两者相同时（从没改过的新项目）才显示创建时间。
  const timeLabel = useMemo(() => {
    const base = updatedAt ? new Date(updatedAt) : new Date(createdAt);
    const rt = relativeTimeOf(base);
    switch (rt.kind) {
      case "justNow":
        return { text: t("timeJustNow"), absolute: base };
      case "minutes":
        return { text: t("timeMinutesAgo", { count: rt.value }), absolute: base };
      case "hours":
        return { text: t("timeHoursAgo", { count: rt.value }), absolute: base };
      case "days":
        return { text: t("timeDaysAgo", { count: rt.value }), absolute: base };
      default:
        return {
          text: new Date(rt.value).toLocaleDateString(locale, {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
          absolute: rt.value,
        };
    }
  }, [updatedAt, createdAt, locale, t]);

  /**
   * 删除（2026-10-10 加闸）。
   *
   * 后端在项目名下有实物时返回 409（`assets.project_id` 是 CASCADE，删项目会连带删素材记录，
   * 而素材库只认「用户名下项目」→ 库会从界面上消失）。这里不再静默失败：
   * 把后端给的「会连带删掉什么」原样摆出来，要用户点第二下（`?force=1`）才真删。
   */
  async function handleDelete(force = false) {
    setDeleting(true);
    try {
      await apiFetch(`/api/projects/${id}${force ? "?force=1" : ""}`, { method: "DELETE" });
      setDeleteOpen(false);
      router.refresh();
    } catch (err) {
      // 🔴 apiFetch 对非 2xx 是**抛** ApiError（不是返回 res）——409 分支必须写在 catch 里，
      //    写在 `res.ok` 判定里根本走不到（2026-10-10 实测踩过一次）。
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { code?: string; counts?: Record<string, number> } | undefined;
        setBlocked({ code: body?.code ?? "PROJECT_HAS_DATA", counts: body?.counts ?? {} });
      } else {
        throw err;
      }
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Link
        href={`/${locale}/project/${id}/episodes`}
        data-project-card={id}
        data-project-stage={stage}
        className="group relative flex flex-col overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-white transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[0_8px_28px_rgba(0,0,0,0.09)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {/* ══ 封面区 ══════════════════════════════════════════════
            改版前整卡是纯文字的：17 张卡并排时整屏只有灰底黑字，
            用户反馈「元素单一、缺乏美观感」。库里其实早就有图（见
            lib/projects/project-cover.ts 的实测数据），只是从没取过。

            16:9 是项目画幅的多数值；用固定 aspect 而不是 w-full，
            是为了让**所有卡片等高** —— 网格里高度不齐会让整屏显得更乱。*/}
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-[var(--surface)]">
          {coverSrc ? (
            <>
              <img
                src={coverSrc}
                alt=""
                loading="lazy"
                decoding="async"
                data-project-cover
                onError={() => setCoverBroken(true)}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              />
              {/* 底部压暗：白字角标与进度要压在图上，没有这层就完全读不清。
                  高度只给44%，避免整张封面被染色失真。 */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 h-11 bg-gradient-to-t from-black/65 via-black/25 to-transparent"
              />
            </>
          ) : (
            /* 无封面占位：阶段色渐变 + 标题首字，比空白块更像"有意为之" */
            <div
              data-project-cover-fallback
              className={`flex h-full w-full flex-col items-center justify-center gap-1 bg-gradient-to-br ${fallbackGradient}`}
            >
              <Clapperboard className="h-6 w-6 text-white/70" />
              <span className="max-w-[85%] truncate px-2 text-[11px] font-medium text-white/85">
                {title}
              </span>
            </div>
          )}

          {/* 阶段徽章压在封面左上：这是「这项目做到哪了」的第一答案，
              放在图上比放在正文里更早被看到（视线先落在图像上）。
              🔴 图标一律静态：阶段是「进度状态」不是「运行状态」，
              转圈动画会让静态项目看起来像正在渲染（2026-10-07 用户反馈）。 */}
          <span
            className={`absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset backdrop-blur-sm ${style.chip}`}
          >
            <StageIcon className="h-3 w-3" />
            {t(`stage.${stage}`)}
          </span>

          {/* 画幅 / 生成模式：两个 2~3 字的规格，右上角一行。
              它们决定「点进去之后片子长什么样」，是选项目时的关键筛选条件。 */}
          <span className="absolute right-2 top-2 flex items-center gap-1">
            {aspectRatio ? (
              <span
                title={t("aspectRatio")}
                className="inline-flex items-center gap-0.5 rounded-md bg-black/45 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-white/90 backdrop-blur-sm"
              >
                <Ratio className="h-2.5 w-2.5" />
                {aspectRatio}
              </span>
            ) : null}
            <span
              title={
                generationMode === "keyframe"
                  ? t("modeKeyframe")
                  : t("modeReference")
              }
              className="inline-flex items-center gap-0.5 rounded-md bg-black/45 px-1.5 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm"
            >
              {generationMode === "keyframe" ? (
                <Frame className="h-2.5 w-2.5" />
              ) : (
                <Scan className="h-2.5 w-2.5" />
              )}
              {generationMode === "keyframe"
                ? t("modeKeyframeShort")
                : t("modeReferenceShort")}
            </span>
          </span>

          {/* 封面来源角标（底部居中）：告诉用户这张图是"产出的画面"还是"一张素材"，
              不必点进去才知道项目完成度。

              🔴 居中而不是左下：左下已经被悬停时滑入的「进入」箭头占了。
              两者若都在左下，静态时看似不冲突，但一悬停箭头就压在角标上 ——
              这种"平时没事、交互时才炸"的布局最难排查。居中后互不干涉。 */}
          {cover && coverSrc ? (
            <span className="absolute bottom-2 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-md bg-black/50 px-1.5 py-0.5 text-[10px] text-white/85 backdrop-blur-sm">
              <Sparkles className="h-2.5 w-2.5" />
              {t(COVER_SOURCE_LABEL_KEY[cover.source])}
            </span>
          ) : null}

          {/* 更多菜单放封面右下（与画幅徽章同一行的下一层）——
              放在这里是因为正文区要塞指标网格，菜单挤在标题旁会挤掉标题宽度。
              半透明底 + blur：不透明白底会在深色封面上开一个刺眼的洞。

              🔴 悬停才显形：菜单里目前只有「删除」这一项，
              而删除是低频且带二次确认的操作 —— 常驻一个实心按钮在17 张卡里
              划过 17 个删除入口，视觉噪音远大于收益。`opacity-0` + `group-hover`
              让它平时隐身、鼠标进入卡片才出现（触屏走 focus-within）。*/}
          <span
            className="absolute bottom-2 right-2 z-10 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100"
            onClick={(e) => e.preventDefault()}
          >
            <MoreMenu
              label={t("moreActions")}
              /* 触发器在封面右下角，向上展开：不遮下方标题与指标格 */
              placement="top"
              items={[
                {
                  key: "delete",
                  label: tc("delete"),
                  icon: <Trash2 className="h-3.5 w-3.5" />,
                  onSelect: () => setDeleteOpen(true),
                },
              ]}
            />
          </span>

          {/* 「进入」箭头：悬停时从右侧滑入。
              原来它在正文底部占一整行（和��间一起），是指纹之外最没必要的一行 ——
              整卡都是链接，这个箭头的唯一作用是提示"可点"，
              而卡片已经有 hover 上浮 + 阴影 + 封面缩放三重反馈了。
              移到封面后：静态零占位，悬停才出现，且落在「眼睛看图」的落点上。*/}
          <span
            aria-hidden
            className="pointer-events-none absolute bottom-2 left-2 z-10 flex h-6 w-6 -translate-x-1 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur-sm transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100"
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </div>

        {/* ══ 正文区 ══════════════════════════════════════════════
            紧凑化（2026-10-07 二轮）：一版改版解决了「内容太少」，
            但 MetricCell 沿用「图标行 + 数字行」两层竖排，六格就是三四行高度；
            底部还单占一行放时间。实测整卡正文高达 270px，
            4列 栅格下一屏只能看到 1.5 行卡片 —— 密度上去了，节奏没上去。

            本轮三个动作，每个都对应一处真实冗余：
            ① 指标格「图标+数字+标签」三层 → **单行横排**（省约 24px）
            ② 时间从底部独占一行 → **并入标题行右侧**（省一整行约 32px）
            ③ 进度条的标签行与数值行合并、数值右对齐同排（每组省约 8px）
            合计正文约 270px → 180px，同屏可见卡片从 1.5 行提到 2.5 行。*/}
        <div className="flex flex-1 flex-col gap-2 p-2.5">
          {/* ── 标题 + 时间（同一行）────────────────────────────
              时间是「这个项目还在动吗」的答案，但它本身信息量低，
              独占一行会让 17 张卡的底部时间参差不齐（改版前的原问题）。
              并到标题行右侧后：① 省一行高度 ② 时间与标题同为「识别信息」，
              视线横扫时先读标题、再顺手看到新鲜度，语义相邻。*/}
          <div className="flex items-start justify-between gap-2">
            <h3 className="min-w-0 flex-1 font-display text-[13px] font-semibold leading-snug text-[var(--text-primary)]">
              {/* 两行截断而不是一行 truncate：标题是这屏上唯一逐张不同的信息，
                  长标题被砍成「末世第一天，丈母娘逼我离婚，我亮出…」就认不出来了。
                  line-clamp 保留两行 + full title 属性兜底可读全文。 */}
              <span className="line-clamp-2" title={title}>
                {title}
              </span>
            </h3>
            <span
              className="shrink-0 pt-px text-[10px] leading-4 text-[var(--text-muted)]"
              title={timeLabel.absolute.toLocaleString(locale)}
            >
              {timeLabel.text}
            </span>
          </div>

          {/* ── 风格三件套（色调 / 情绪 / 时代）────
              库里多数项目这三个字段有值（如「低饱和莫兰迪/ 荒诞黑色幽默 / 80年代香港」），
              它们是「这是个什么项目」最直接的描述，比 status 有信息量得多。
              用色块 chip 而不是灰字：这是全卡唯一允许出现彩色文字的地方，
              用来在一屏灰白里提供视觉锚点。

              🔴 只显示前两个：三件套全显示时在 300px 宽的卡里会折行，
              行高一变整卡高度就参差 —— 而参差正是「松散感」的另一半来源。
              第三个进 title，悬停可见，信息不丢。 */}
          {props.colorPalette || props.mood || props.eraAesthetics ? (
            <div className="flex min-w-0 gap-1 overflow-hidden">
              {props.eraAesthetics ? (
                <StyleChip tone="amber">{props.eraAesthetics}</StyleChip>
              ) : null}
              {props.mood ? <StyleChip tone="violet">{props.mood}</StyleChip> : null}
              {props.colorPalette ? (
                <StyleChip tone="teal">{props.colorPalette}</StyleChip>
              ) : null}
            </div>
          ) : artStyle ? (
            /* 三件套全空时退回 artStyle（视觉风格），保持"总有东西说明风格"。
               🔴 2026-10-11：artStyle 存的是 slug（如 chinese-urban-realistic），
               卡片上必须显示解析后的中文名，否则可读性直接塌掉。 */
            <p
              title={resolveArtStyleLabel(artStyle)}
              className="flex items-start gap-1 text-[11px] leading-4 text-[var(--text-muted)]"
            >
              <Palette className="mt-px h-3 w-3 shrink-0" />
              <span className="line-clamp-1">{resolveArtStyleLabel(artStyle)}</span>
            </p>
          ) : null}

          {/* ── 指标网格：3×2 六格 ──────────────────────────────
              改版前是三个 chip 挤在一行，视觉上只是一串小数字；
              改成 3 列 × 2 行后，每格有独立的图标 + 数字 + 单位三层，
              扫视时能形成"表格感"，信息层级立刻清晰。

              六格选哪六个：集/ 角色 / 分镜是规模，场景 / 道具 / 台词是资产量。
              出片数不进网格 —— 它已经在下面的进度条里，重复出现会让人怀疑
              「293 到底是分镜数还是出片数」。*/}
{/* 🔴 列数与栅格联动（2026-10-07 6 列@xl(1280) 档同步）：
              1280@6列 + 指标3列 → 每格仅 47px，装不下「图标 + 数字 + 『个分镜』」的
              ~72px，单位折行、整排卡高度参差。所以 ≥1280px（6 列档）把指标
              从 3列×2行 切成 2列×3行，每格 74px，稳放。
              多一行 +16px 由封面变矮抵消（4列@1280 封面 165px → 6列 107px）。
              断点值必须与 project-grid.tsx 的 6 列档一致（门禁有联动断言）。*/}
          <dl className="grid grid-cols-3 gap-x-1.5 gap-y-1 rounded-lg bg-[var(--surface)]/70 px-2 py-1.5 xl:grid-cols-2">
            <MetricCell icon={Clapperboard} value={episodeCount} label={t("statEpisodes")} />
            <MetricCell icon={UserRound} value={characterCount} label={t("statCharacters")} />
            <MetricCell icon={Film} value={shotCount} label={t("statShots")} />
            <MetricCell icon={Layers} value={sceneCount} label={t("statScenes")} />
            <MetricCell icon={Frame} value={propCount} label={t("statProps")} />
            <MetricCell
              icon={MessageSquare}
              value={dialogueCount}
              label={t("statDialogues")}
            />
          </dl>

          {/* ── 双进度：集完成度 + 出片进度 ────────────────────
              两条进度回答两个不同问题：
                出片进度 =「镜头出了多少条片子」← 工作量完成度
                集完成度 =「多少集成片了」      ← 成品完成度
              实测这两个数字会严重背离（旺仔码头2：出片 62% 但成片只有 1/12 集），
              只给一条会让用户误判项目进度 —— 所以两条都留。

              任一计数为 0 时不画那条：0 集的纯剧本项目画一条 0% 的集进度纯属噪声。

              mt-auto 仍保留：正文区可能没有风格行（无风格的项目），
              没有它，进度组会紧贴指标格而不是沉到卡底。*/}
          <div className="mt-auto space-y-1.5 pt-0.5">
            {shotCount > 0 ? (
              <ProgressRow
                label={t("statVideoProgress")}
                value={`${formatCount(videoShotCount)}/${formatCount(shotCount)}`}
                percent={videoProgress}
                bar={style.bar}
              />
            ) : null}
            {episodeCount > 0 ? (
              <ProgressRow
                label={t("statEpisodeProgress")}
                value={`${formatCount(finishedEpisodeCount)}/${formatCount(episodeCount)}`}
                percent={episodeProgress}
                bar={style.bar}
                muted={shotCount === 0}
              />
            ) : null}
          </div>
        </div>
      </Link>

      <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setBlocked(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("deleteConfirmTitle")}</DialogTitle>
            <DialogDescription>{t("deleteConfirmDesc", { title })}</DialogDescription>
          </DialogHeader>
          {/* 二次确认块：只有后端真的拦下来才出现（默认路径完全不变） */}
          {blocked && (
            <div
              role="alert"
              data-delete-blocked={blocked.code}
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900"
            >
              {blocked.code === "PROJECT_OWNS_LIBRARY"
                ? t("deleteLibraryBlocked")
                : t("deleteBlockedHint", {
                    assets: blocked.counts.assets ?? 0,
                    shots: blocked.counts.shots ?? 0,
                    episodes: blocked.counts.episodes ?? 0,
                  })}
            </div>
          )}
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>{tc("cancel")}</DialogClose>
            {blocked?.code === "PROJECT_OWNS_LIBRARY" ? (
              // 素材库宿主：不给「仍要删除」，避免从卡片上把整个库抹掉
              <Button variant="destructive" disabled>
                {tc("delete")}
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => void handleDelete(blocked !== null)}
                disabled={deleting}
              >
                {deleting ? tc("loading") : blocked ? t("deleteForce") : tc("delete")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * 指标格：**图标 + 数字 + 单位，单行横排**。
 *
 * ## 为什么从三层竖排改成单行（2026-10-07 二轮）
 *
 * 一版改版把它做成「图标行 / 数字行」两层竖排，理由是"形成表格感"。
 * 但实测六格 = 2 行 × 2 层 = 4 条文本行，加上 gap 与内边距吃掉近 90px，
 * 而这个高度换来的收益很有限 —— 3×2 的**列对齐**才是"表格感"的来源，
 * 与层数无关。所以这里保留列对齐、砍掉层数：
 *
 *   竖排（改版一）        横排（改版二）
 *   ┌────────┐              ┌────────┐
 *   │ 🎬 剧集 │              │ 🎬 21 集│
 *   │ 21     │              └────────┘
 *   └────────┘
 *   2 行 ≈ 32px            1 行 ≈ 16px
 *
 * 图标降到 10px 并用 muted 色：它只是「这格是什么」的锚点，不该跟数字抢注意力。
 * 数字保持 `font-semibold` + `tabular-nums` —— 竖排时它是独立一行，横排后
 * 它变成这一格里唯一的粗体，层级反而更清楚。
 */
function MetricCell({
  icon: Icon,
  value,
  label,
}: {
  icon: typeof Film;
  value: number;
  label: string;
}) {
  return (
    <div className="flex min-w-0 items-baseline gap-1">
      <Icon className="h-2.5 w-2.5 shrink-0 translate-y-px text-[var(--text-muted)]/70" />
      {/* 0 不用透明度压到几乎看不见 —— 那会让"这个项目没有角色"看起来像 bug。
          改成 0 用 muted 灰、>0 用次级墨色，语义清晰。数字本身不透明度也随 0 降一档。*/}
      <dd
        className={`text-[12px] font-semibold leading-4 tabular-nums ${
          value > 0 ? "text-[var(--text-secondary)]" : "text-[var(--text-muted)]/50"
        }`}
      >
        {formatCount(value)}
      </dd>
      <dt className="min-w-0 truncate text-[10px] leading-4 text-[var(--text-muted)]">
        {label}
      </dt>
    </div>
  );
}

/**
 * 进度行：标签 + 分数 + 色条。两条进度（出片 / 成片）共用，所以抽出来。
 *
 * 二轮紧凑化：轨道从 `h-1.5` 降到 `h-1`，标签行与数值行仍同行但行高压缩。
 * 1px 轨道在 300px 宽的卡里依然清晰（实测 2px 时两条进度挨太近像双线），
 * 而四条 1px 轨道比四条 1.5px 轨道省下的 4px 正好够放风格 chip 少折一行的余量。
 */
function ProgressRow({
  label,
  value,
  percent,
  bar,
  muted = false,
}: {
  label: string;
  value: string;
  percent: number;
  bar: string;
  /** 次要进度（集进度在没有分镜时）整体降一档视觉权重 */
  muted?: boolean;
}) {
  const pct = Math.round(Math.min(1, Math.max(0, percent)) * 100);
  return (
    <div className={muted ? "opacity-70" : undefined}>
      <div className="flex items-baseline justify-between text-[10px] leading-3 text-[var(--text-muted)]">
        <span className="truncate">{label}</span>
        <span className="shrink-0 tabular-nums">
          {value}
          <span className="ml-1 font-medium text-[var(--text-secondary)]">{pct}%</span>
        </span>
      </div>
      {/* 轨道色要能看见：原来用 --surface，在白底上几乎不可见，
          于是「0% 时那个 2% 的小圆点」看着像个坏掉的元素而不是进度条。
          现在 0% 就是真的空条（轨道仍在），语义与视觉一致。 */}
      <div className="mt-1 h-1 overflow-hidden rounded-full bg-[var(--border-subtle)]/70">
        <div
          className={`h-full rounded-full transition-all duration-500 ${bar}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** 风格 chip 的三套配色（色调/情绪/时代各一），刻意分散色相以便一眼区分。 */
const STYLE_TONE: Record<"amber" | "violet" | "teal", string> = {
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  violet: "bg-violet-50 text-violet-700 ring-violet-200",
  teal: "bg-teal-50 text-teal-700 ring-teal-200",
};

function StyleChip({
  tone,
  children,
}: {
  tone: keyof typeof STYLE_TONE;
  children: string;
}) {
  return (
    <span
      title={children}
      className={`inline-flex min-w-0 max-w-full shrink items-center rounded px-1.5 py-px text-[10px] leading-4 ring-1 ring-inset ${STYLE_TONE[tone]}`}
    >
      <span className="truncate">{children}</span>
    </span>
  );
}
